// src/components/lesson/diagrams/Pyramid.tsx
//
// Right square pyramid for SAT Volume questions: V = (1/3)·(base area)·h (the
// (1/3)ℓwh form is on the Digital SAT reference sheet). Drawn in oblique
// projection: a parallelogram base, apex above the base center, four lateral
// edges (the hidden back-left edge dashed), and a dashed height.
//
// Params (all optional):
//   base?: number, height?: number     // proportional (base = side length)
//   baseLabel?: string, heightLabel?: string
//   showHiddenEdges?: boolean (default true)
//   label?: string

type Params = {
  base?: number
  height?: number
  baseLabel?: string
  heightLabel?: string
  showHiddenEdges?: boolean
  label?: string
}

const W = 340
const H = 260
const PAD = 34
const stroke = 'rgb(34 211 238)'
const fill = 'rgb(34 211 238 / 0.08)'
const labelColor = 'rgb(251 191 36)'
const strokeW = 1.8

export default function Pyramid({ params }: { params: Record<string, unknown> }) {
  const p = params as Params
  const base = typeof p.base === 'number' && p.base > 0 ? p.base : 4
  const height = typeof p.height === 'number' && p.height > 0 ? p.height : 5
  const showHidden = p.showHiddenEdges !== false

  const usableW = W - 2 * PAD
  const usableH = H - 2 * PAD
  // base → front width; depth offset is a fixed fraction of the width.
  const scale = Math.min(usableW / (base * 1.3), usableH / (height + base * 0.25))
  const bw = base * scale
  const ph = height * scale
  const dx = bw * 0.3
  const dy = bw * 0.22

  // Center the whole figure (front width bw + depth skew dx).
  const leftX = (W - (bw + dx)) / 2
  const baseCy = H - PAD - dy * 0.5
  const FL = { x: leftX, y: baseCy }
  const FR = { x: leftX + bw, y: baseCy }
  const BR = { x: leftX + bw + dx, y: baseCy - dy }
  const BL = { x: leftX + dx, y: baseCy - dy }
  const center = { x: (FL.x + FR.x + BR.x + BL.x) / 4, y: (FL.y + FR.y + BR.y + BL.y) / 4 }
  const apex = { x: center.x, y: baseCy - dy - ph }

  const line = (a: { x: number; y: number }, b: { x: number; y: number }, dashed = false) => (
    <line
      x1={a.x}
      y1={a.y}
      x2={b.x}
      y2={b.y}
      stroke={stroke}
      strokeWidth={dashed ? strokeW * 0.7 : strokeW}
      strokeDasharray={dashed ? '4 3' : undefined}
    />
  )

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Square pyramid'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Filled front faces */}
      <polygon
        points={`${apex.x},${apex.y} ${FL.x},${FL.y} ${FR.x},${FR.y}`}
        fill={fill}
        stroke="none"
      />
      <polygon
        points={`${apex.x},${apex.y} ${FR.x},${FR.y} ${BR.x},${BR.y}`}
        fill={fill}
        stroke="none"
      />

      {/* Base edges: front + right solid, back + left dashed (hidden) */}
      {line(FL, FR)}
      {line(FR, BR)}
      {showHidden && line(BR, BL, true)}
      {showHidden && line(BL, FL, true)}

      {/* Lateral edges: three visible, back-left dashed */}
      {line(apex, FL)}
      {line(apex, FR)}
      {line(apex, BR)}
      {showHidden && line(apex, BL, true)}

      {/* Height — dashed from apex to base center */}
      <line
        x1={apex.x}
        y1={apex.y}
        x2={center.x}
        y2={center.y}
        stroke={labelColor}
        strokeWidth={strokeW * 0.7}
        strokeDasharray="4 3"
      />
      <text
        x={apex.x + 6}
        y={(apex.y + center.y) / 2}
        fontSize="12"
        fontWeight="600"
        fill={labelColor}
      >
        {p.heightLabel ?? 'h'}
      </text>

      {/* Base side label on the front edge */}
      <text
        x={(FL.x + FR.x) / 2}
        y={FL.y + 16}
        fontSize="12"
        fontWeight="600"
        textAnchor="middle"
        fill={labelColor}
      >
        {p.baseLabel ?? 's'}
      </text>
    </svg>
  )
}
